"use client";

import React, { useState, useMemo, useRef } from "react";
import Link from "next/link";
import { formatDate } from "@/utils/commonFunctions";
import Icon from "@/components/icons/Icons";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { AskedQuestion } from "@/type/commonTypes";

import Lottie from "lottie-react";
import notFoundAnimation from "@/../public/next_assets/lottie/not-found-blogs.json";

interface QuestionsSectionProps {
  questions: AskedQuestion[];
}

type SortOptionValue = "newest" | "most_viewed" | "most_answered" | "oldest";

type SortOption = {
  value: SortOptionValue;
  label: string;
};

const SORT_OPTIONS: SortOption[] = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "most_viewed", label: "Most Viewed" },
  { value: "most_answered", label: "Most Answered" },
];
const ITEMS_PER_PAGE = 8;

export default function QuestionsSection({ questions }: QuestionsSectionProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>(SORT_OPTIONS[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isActiveDropdown, setIsActiveDropdown] = useState(false);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLButtonElement>(null);

  // Filter questions based on search input
  const filteredQuestions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return questions;

    return questions.filter((q) => {
      const qText = (q.question || q.txttitle || "").toLowerCase();
      const author = (q.pagename || "").toLowerCase();
      return qText.includes(term) || author.includes(term);
    });
  }, [questions, searchTerm]);

  // Sort filtered questions
  const sortedQuestions = useMemo(() => {
    const list = [...filteredQuestions];

    switch (sortBy.value) {
      case "most_viewed":
        return list.sort((a, b) => (b.page_view || 0) - (a.page_view || 0));
      case "most_answered":
        return list.sort(
          (a, b) => (b.answer_count || 0) - (a.answer_count || 0),
        );
      case "oldest":
        return list.sort((a, b) => {
          const timeA = a.date
            ? new Date(a.date.replace(" ", "T")).getTime()
            : 0;
          const timeB = b.date
            ? new Date(b.date.replace(" ", "T")).getTime()
            : 0;
          return timeA - timeB;
        });
      case "newest":
      default:
        return list.sort((a, b) => {
          const timeA = a.date
            ? new Date(a.date.replace(" ", "T")).getTime()
            : 0;
          const timeB = b.date
            ? new Date(b.date.replace(" ", "T")).getTime()
            : 0;
          if (timeA !== timeB) return timeB - timeA;
          return b.id - a.id;
        });
    }
  }, [filteredQuestions, sortBy]);

  // Pagination calculation
  const totalItems = sortedQuestions.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalItems);
  const currentQuestions = sortedQuestions.slice(startIndex, endIndex);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleSortChange = (selectedOption: SortOption) => {
    setSortBy(selectedOption);
    setCurrentPage(1);
  };

  const goToPage = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    if (listContainerRef.current) {
      listContainerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <div ref={listContainerRef} className="w-full">
      {/* 100% Width Search Bar & Sort Controls */}
      <div className="w-full mb-6 space-y-3">
        <div className="w-full flex justify-between gap-2 sm:gap-6">
          {/* Full-width Search Input */}
          <div className="group relative w-full flex items-center gap-3 bg-white border border-slate-200 rounded-2lg px-3 py-2 transition sm:hover:border-primaryBlue/20">
            {/* Search Icon SVG */}
            <Icon.searchIcon className="text-primaryBlack/80 sm:group-hover:text-primaryBlack" />

            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search questions by topic, keyword, or user..."
              className="w-full bg-transparent text-sm sm:text-base text-slate-900 placeholder-slate-400 outline-hidden"
            />

            {searchTerm && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="p-1 ml-auto"
                title="Clear search"
                aria-label="Clear search"
              >
                <Icon.crossIcon className="w-4 h-4 text-primaryBlack/80 sm:group-hover:text-primaryBlack" />
              </button>
            )}
          </div>

          {/* Sort Dropdown */}
          <div className="w-full max-w-64 relative">
            <button
              ref={dropdownRef}
              onClick={() => setIsActiveDropdown(!isActiveDropdown)}
              aria-label="Select to Open Dropdown to select the City"
              className="w-full flex items-center justify-between gap-5 border rounded-2lg px-4 py-3 text-sm sm:text-base sm:leading-1 text-primaryBlue/80 transition-colors sm:hover:text-primaryBlue"
            >
              {sortBy.label}
              <Icon.dropdownDownArrow
                className={`shrink-0 transition-transform ${isActiveDropdown ? "rotate-180" : ""}`}
              />
            </button>

            <CustomDropdown
              isOpen={isActiveDropdown}
              onClose={() => setIsActiveDropdown(false)}
              excludeRefs={[dropdownRef]}
              className="absolute top-full left-0 w-full max-w-xs mx-auto mt-1 rounded-2lg border border-[#cacaca] bg-black/10 backdrop-blur-md shadow-md z-1 p-1"
            >
              <div className="space-y-1">
                {SORT_OPTIONS.map((sortOption: SortOption) => (
                  <button
                    key={sortOption.value}
                    onClick={() => {
                      handleSortChange(sortOption);
                      setIsActiveDropdown(false);
                    }}
                    className={[
                      "block w-full text-left text-2xs sm:text-sm px-3 py-2 rounded-lg",
                      sortOption.value === sortBy.value
                        ? "text-primaryBlue bg-primaryBlue/5"
                        : "text-primaryBlack transition sm:hover:bg-primaryBlue/5 sm:hover:text-primaryBlue",
                    ].join(" ")}
                  >
                    {sortOption.label}
                  </button>
                ))}
              </div>
            </CustomDropdown>
          </div>
        </div>

        {/* Sub-bar: Result counter & Sort control */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-1 pt-1">
          <p className="text-xs sm:text-sm text-slate-500">
            {searchTerm.trim() && (
              <span>
                Found{" "}
                <span className="font-semibold text-slate-900">
                  {totalItems}
                </span>{" "}
                result{totalItems === 1 ? "" : "s"} for &ldquo;
                <span className="font-semibold text-blue-600">
                  {searchTerm}
                </span>
                &rdquo;
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Questions List */}
      {currentQuestions.length === 0 ? (
        <div className="w-full bg-white rounded-2xl border border-slate-200 p-10 sm:p-14 text-center shadow-xs">
          <div className="w-14 h-14 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-4">
            <Lottie
              className="w-32 h-32 mx-auto"
              animationData={notFoundAnimation}
              loop={true}
              autoplay
            />
          </div>
          <p className="text-lg font-bold text-slate-900 mb-1">
            No questions found
          </p>
          <p className="text-sm text-slate-500 max-w-md mx-auto mb-5">
            We couldn&apos;t find any questions matching &ldquo;{searchTerm}
            &rdquo;. Try another search or clear filters to view all questions.
          </p>
          <button
            type="button"
            onClick={handleClearSearch}
            className="px-5 py-2.5 bg-blue-700 hover:bg-blue-800 text-white font-semibold text-sm rounded-xl transition shadow-xs cursor-pointer"
          >
            Clear Search
          </button>
        </div>
      ) : (
        <div className="space-y-4 sm:space-y-5">
          {currentQuestions.map((q) => {
            const author = q.pagename || "Anonymous";
            const initial = author.charAt(0).toUpperCase() || "Q";
            const formattedDate = formatDate(q.date);
            const questionText = q.question || q.txttitle || "";
            const href = `/questions/${q.permalinks || q.id}`;

            return (
              <Link
                key={q.id}
                href={href}
                className="block group rounded-xl sm:rounded-6xl border border-slate-200 bg-white p-4 sm:p-5 transition sm:hover:bg-[#f7faff] sm:hover:border-[#dce4f0] sm:hover:shadow-[0px_4px_16px_0px_#0000000D]"
              >
                {/* Header: Author Avatar, Name & Date */}
                <div className="flex items-center gap-3 mb-3">
                  {/* Avatar Icon Space */}
                  <div className="w-8 h-8 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center shrink-0 uppercase">
                    {initial}
                  </div>

                  <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-2xs sm:text-2sm text-slate-500">
                    <span className="font-semibold text-slate-900">
                      {author}
                    </span>
                    {formattedDate && (
                      <>
                        <span className="text-slate-300">•</span>
                        <span className="inline-flex items-center gap-1 text-xs sm:text-2xs text-slate-500">
                          {formattedDate}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Question: Blue Title, line-clamp-2, title attribute on tag, div/p tag */}
                <p
                  title={questionText}
                  className="text-base sm:text-lg font-bold text-primaryBlue/80 group-hover:text-primaryBlue transition-colors line-clamp-2 mb-4"
                >
                  {questionText}
                </p>

                {/* Footer: Answer count and Views with SVG icons */}
                <div className="flex items-center gap-5 text-xs text-[#6784B3] pt-3.5 border-t border-slate-100">
                  {/* Answer Count with Message Icon SVG */}
                  <div className="inline-flex items-center gap-1.5 font-medium">
                    <Icon.messageIcon className="w-4 h-4 shrink-0" />
                    {q.answer_count || 0}
                    {q.answer_count === 1 ? " answer" : " answers"}
                  </div>

                  {/* Views Count with Eye Icon SVG */}
                  <div className="inline-flex items-center gap-1.5 font-medium">
                    <Icon.viewEyeIcon className="w-4 h-4 shrink-0" />
                    {(q.page_view || 0).toLocaleString()} views
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-4 mt-8 pt-6 border-t border-slate-200">
          <p className="text-xs sm:text-sm text-slate-500">
            Page{" "}
            <span className="font-semibold text-slate-900">{currentPage}</span>{" "}
            of{" "}
            <span className="font-semibold text-slate-900">{totalPages}</span>
          </p>

          <div className="flex items-center gap-1.5">
            {/* Previous Button */}
            <button
              type="button"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${
                currentPage === 1
                  ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 hover:border-slate-300"
              }`}
              aria-label="Previous page"
            >
              {/* Left Arrow SVG */}
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span>Prev</span>
            </button>

            {/* Page Number Buttons */}
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (pageNum) => {
                // Show smart page numbers if many pages (e.g. 1, 2, ..., last)
                const isFirst = pageNum === 1;
                const isLast = pageNum === totalPages;
                const isNearCurrent = Math.abs(pageNum - currentPage) <= 1;

                if (!isFirst && !isLast && !isNearCurrent) {
                  if (pageNum === 2 || pageNum === totalPages - 1) {
                    return (
                      <span
                        key={pageNum}
                        className="px-1.5 text-xs text-slate-400"
                      >
                        ...
                      </span>
                    );
                  }
                  return null;
                }

                const isActive = currentPage === pageNum;

                return (
                  <button
                    key={pageNum}
                    type="button"
                    onClick={() => goToPage(pageNum)}
                    className={`w-8 h-8 rounded-lg text-xs font-semibold transition cursor-pointer flex items-center justify-center ${
                      isActive
                        ? "bg-blue-700 text-white shadow-xs"
                        : "bg-white text-[#475467] border border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {pageNum}
                  </button>
                );
              },
            )}

            {/* Next Button */}
            <button
              type="button"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${
                currentPage === totalPages
                  ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 hover:border-slate-300"
              }`}
              aria-label="Next page"
            >
              <span>Next</span>
              {/* Right Arrow SVG */}
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
